import { useEffect } from "react";
import { Dialog, Slide, Button, Switch } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import React from "react";
import Spinner from "../../components/spinner";
import MuiAlert from "@mui/material/Alert";
import Editor from "../../components/elements/Editor";
import ReactDatePicker from "react-datepicker";
import useCreate from "../../hooks/useCreate";
import toast from "react-hot-toast";
import useBlogForm from "../../hooks/blog/useBlogForm";
import useBlogImage from "../../hooks/blog/useBlogImage";
import useBlogSlug from "../../hooks/blog/useBlogSlug";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const IS_REQUIRED = true;

function UpdateBlogs({ open, handleClose, blog_id }) {
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);

  // Use the blog form hook
  const {
    blogFormData,
    isLoading,
    isSubmitting,
    isError,
    isSuccess,
    handleBlogFormData,
    handleBlogSeo,
    handleImageAlt,
    handleChangeEditor,
    handleSetPublishDate,
    handleBlogStatus,
    handleSubmitBlogFormData,
    closeErrorMessage,
    updateImageData,
    updateThumbnailData,
    clearImageData,
    clearThumbnailData,
  } = useBlogForm({
    blog_id,
    isEditMode: true,
    onClose: handleClose,
  });

  // Use the blog image hook for banner
  const {
    fileInputRef,
    imagePreview,
    setImagePreview,
    isUploading,
    handleOpenFileUploadDialog,
    handleUploadFile,
    handleDeleteImage: handleDeleteBanner,
  } = useBlogImage({
    onImageUploaded: (document_id, imageUrl) => {
      updateImageData(document_id, imageUrl);
    },
    onImageDeleted: clearImageData,
  });

  // Use a separate blog image hook for thumbnail
  const {
    fileInputRef: thumbnailFileInputRef,
    imagePreview: thumbnailPreview,
    setImagePreview: setThumbnailPreview,
    isUploading: isThumbnailUploading,
    handleOpenFileUploadDialog: handleOpenThumbnailUploadDialog,
    handleUploadFile: handleUploadThumbnail,
    handleDeleteImage: handleDeleteThumbnail,
  } = useBlogImage({
    onImageUploaded: (_, imageUrl) => {
      // For thumbnail, we only need the URL path, not the document_id
      updateThumbnailData(imageUrl);
    },
    onImageDeleted: clearThumbnailData,
  });

  // Set image preview when the form data loads
  useEffect(() => {
    if (blogFormData.path) {
      setImagePreview(blogFormData.path);
    }
    if (blogFormData.thumbnail) {
      setThumbnailPreview(blogFormData.thumbnail);
    }
  }, [blogFormData.path, blogFormData.thumbnail, setImagePreview, setThumbnailPreview]);

  // Use the blog slug hook
  const {
    slug,
    openSlugModal,
    isOpen,
    isSubmitting: isSubmittingSlug,
    handleSlugChange,
    handleOpenSlug,
    handleCloseSlugModal,
    toggleButton,
    handleSubmitSlug,
  } = useBlogSlug({
    blog_id,
    initialSlug: blogFormData.slug,
    onSuccess: (newSlug) => {
      // Update the form data with the new slug
      updateImageData({ ...blogFormData, slug: newSlug });
    },
  });

  // Handle blog deletion
  const { setDataToServer: setDeleteData } = useCreate({
    url: "delete-blog",
    refreshUrl: "get-blog-list",
    onSuccess: () => {
      toast.success("Blog Deleted Successfully", {
        position: "top-right",
        duration: 3000,
      });
      handleClose();
    },
    onError: (err) => {
      console.error(err);
      toast.error("Oops! Something went wrong", {
        position: "top-right",
        duration: 3000,
      });
    },
  });

  const handleOpenDeleteModal = () => {
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const handleDeleteBlog = (e) => {
    e.preventDefault();
    setDeleteData.mutate({
      blog_id: blog_id
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <Dialog fullScreen open={open} TransitionComponent={Transition}>
        <div className="container_xxl px-5 py-10 flex justify-center items-center">
          <Spinner />
        </div>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        fullScreen
        fullWidth={true}
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        disableEnforceFocus={true}
        disableAutoFocus={true}
      >
        <div className="container_xxl px-5">
          <div className="flex items-center space-x-4 headingBorder__b sticky top-0 z-40 bg-white">
            <div>
              <IconButton onClick={handleClose}>
                <ArrowBackIcon />
              </IconButton>
            </div>
            <div>
              <h1 className="heading">Update Blog</h1>
            </div>
          </div>

          <div className="form-body py-8">
            <form
              className=""
              autoComplete="off"
              onSubmit={handleSubmitBlogFormData}
            >
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="title" className="label">
                    Blog Title <small className="text-red-700">*</small>
                  </label>
                  <input
                    type="text"
                    required={IS_REQUIRED}
                    placeholder="Enter Blog Title"
                    value={blogFormData.title}
                    onChange={handleBlogFormData}
                    name="title"
                    className="input mt-4"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="label">
                    Blog Description <small className="text-red-700">*</small>
                  </label>
                  <input
                    type="text"
                    required={IS_REQUIRED}
                    placeholder="Enter Blog Description"
                    value={blogFormData.description}
                    onChange={handleBlogFormData}
                    name="description"
                    className="input mt-4"
                  />
                </div>

                <div>
                  <label htmlFor="published_at" className="label">
                    Blog Publish Date <small className="text-red-700">*</small>
                  </label>
                  <div className="w-full mt-4">
                    <ReactDatePicker
                      dateFormat="yyyy/MM/dd"
                      required={IS_REQUIRED}
                      selected={blogFormData.published_at}
                      onChange={(date) => handleSetPublishDate(date)}
                      className="input w-full"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="Blog Image" className="label">
                    Blog Banner Image
                  </label>
                  <small className="text-blue-700">
                    Note: Recommended Size: 1920 x 1080 px (JPG, PNG, JPEG)
                    <br />
                    Use a wide, cinematic crop. The top and bottom will be heavily cropped on the website.
                  </small>

                  {isUploading ? (
                    <Spinner />
                  ) : (
                    <>
                      {imagePreview && (
                        <div className="my-4">
                          <img
                            src={imagePreview}
                            alt="Blog preview"
                            className="object-contain w-[400px] h-auto border mb-2"
                          />
                          <div className="flex gap-2">
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={handleOpenFileUploadDialog}
                              disabled={isUploading}
                            >
                              Change Image
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={handleDeleteBanner}
                              startIcon={<DeleteIcon />}
                            >
                              Remove Image
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div className="mt-3">
                    <label htmlFor="image_alt" className="label">
                      Image Alt Text
                    </label>
                    <input
                      type="text"
                      placeholder="Image Alt Text"
                      value={blogFormData.image_alt}
                      onChange={handleImageAlt}
                      name="image_alt"
                      className="input mt-2"
                    />
                  </div>

                  <div className="mt-4">
                    {!imagePreview && (
                      <Button
                        type="button"
                        variant="contained"
                        color="success"
                        className="primaryBtn-contained"
                        onClick={handleOpenFileUploadDialog}
                        disabled={isUploading}
                      >
                        Add Banner Image
                      </Button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleUploadFile}
                    />
                  </div>
                </div>

                {/* Thumbnail section */}
                <div>
                  <label htmlFor="Blog Thumbnail" className="label">
                    Blog Thumbnail
                  </label>
                  <small className="text-blue-700">
                    Note: Recommended Size: 1600 x 1200 px (JPG, PNG, JPEG)
                  </small>

                  {isThumbnailUploading ? (
                    <Spinner />
                  ) : (
                    <>
                      {thumbnailPreview && (
                        <div className="my-4">
                          <img
                            src={thumbnailPreview}
                            alt="Thumbnail preview"
                            className="object-contain w-[250px] h-auto border mb-2"
                          />
                          <div className="flex gap-2">
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={handleOpenThumbnailUploadDialog}
                              disabled={isThumbnailUploading}
                            >
                              Change Image
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={handleDeleteThumbnail}
                              startIcon={<DeleteIcon />}
                            >
                              Remove Image
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div className="mt-4">
                    {!thumbnailPreview && (
                      <Button
                        type="button"
                        variant="contained"
                        color="success"
                        className="primaryBtn-contained"
                        onClick={handleOpenThumbnailUploadDialog}
                        disabled={isThumbnailUploading}
                      >
                        Add Thumbnail
                      </Button>
                    )}
                    <input
                      ref={thumbnailFileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleUploadThumbnail}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="Blog Content" className="label">
                    Blog Content <small className="text-red-700">*</small>
                  </label>
                  <div className="mt-5">
                    <Editor
                      value={blogFormData.content}
                      onChange={handleChangeEditor}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="Blog Status" className="label mr-4">
                    Blog Status
                  </label>
                  <span className="text-gray-700 text-sm">Hide</span>
                  <Switch
                    checked={blogFormData.status === "1"}
                    inputProps={{ "aria-label": "controlled" }}
                    onChange={handleBlogStatus}
                    className="hide_show_switch"
                  />
                  <span className="text-gray-700 text-sm">Show</span>
                </div>

                <div className="mt-3 flex items-center justify-between border-b border-gray-400">
                  <h1 className="font-semibold text-2xl pb-3">
                    Search Engine Optimization
                  </h1>
                </div>

                <div>
                  <div className="mb-2">
                    <label className="label">Meta Title</label>
                    <input
                      type="text"
                      placeholder="Meta Title"
                      name="seo"
                      data-name="title"
                      className="input mt-4"
                      value={blogFormData.seo?.title || ""}
                      onChange={handleBlogSeo}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="label">Meta Description</label>
                    <input
                      type="text"
                      placeholder="Meta Description"
                      name="seo"
                      data-name="description"
                      className="input mt-4"
                      value={blogFormData.seo?.description || ""}
                      onChange={handleBlogSeo}
                    />
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between border-b border-gray-400">
                  <h1 className="font-semibold text-2xl pb-3">Slug</h1>
                </div>

                <div className="flex space-x-5 mt-4">
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleOpenSlug}
                  >
                    Edit Slug
                  </Button>
                </div>
              </div>

              {isError && (
                <Alert
                  onClose={closeErrorMessage}
                  className="mb-4 mt-4"
                  severity="error"
                  sx={{ width: "100%" }}
                >
                  Error updating blog, Please try again later
                </Alert>
              )}

              {isSuccess && (
                <Alert
                  onClose={closeErrorMessage}
                  className="mb-4 mt-4"
                  severity="success"
                  sx={{ width: "100%" }}
                >
                  Blog Updated Successfully
                </Alert>
              )}

              <div className="text-center flex justify-center space-x-5 my-6">
                {isSubmitting ? (
                  <Spinner />
                ) : (
                  <>
                    <Button
                      type="submit"
                      variant="contained"
                      className="primaryBtn-contained"
                    >
                      Update Blog
                    </Button>
                  </>
                )}
                <Button
                  type="button"
                  color="error"
                  variant="contained"
                  className="primaryBtn-contained"
                  onClick={handleOpenDeleteModal}
                >
                  Delete Blog
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Slug Edit Modal */}
      <Dialog fullWidth={true} maxWidth={"sm"} open={openSlugModal}>
        <div className="bg-white w-full px-5">
          <div className="modal-head flex items-center justify-end pt-2">
            <div onClick={handleCloseSlugModal}>
              <IconButton>
                <CloseIcon />
              </IconButton>
            </div>
          </div>
          <form className="space-y-4" onSubmit={handleSubmitSlug}>
            {isOpen ? (
              <>
                <div className="space-y-7">
                  <div>
                    <h1 className="text-2xl font-semibold text-center">
                      Edit Slug
                    </h1>
                  </div>
                  <div className="flex justify-center space-x-5 ">
                    <input
                      type="text"
                      placeholder="Slug"
                      className="input"
                      name="slug"
                      value={slug}
                      onChange={handleSlugChange}
                    />

                    {isSubmittingSlug ? (
                      <Spinner />
                    ) : (
                      <Button type="submit" variant="contained">
                        Save
                      </Button>
                    )}
                  </div>
                  <div className="flex justify-center pb-5"></div>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-7">
                  <div>
                    <h1 className="text-2xl text-red-700 font-semibold text-center">
                      Are You Sure You Want Edit The Slug?
                    </h1>
                    <h3 className="text-lg text-red-700 font-semibold text-center mt-2">
                      This will affect all the back links and SEO
                    </h3>
                  </div>
                </div>
                <div className="space-x-4 text-center pb-5">
                  <Button
                    type="button"
                    variant="contained"
                    color={`error`}
                    onClick={toggleButton}
                  >
                    Proceed
                  </Button>

                  <Button
                    variant="outlined"
                    color="info"
                    onClick={handleCloseSlugModal}
                  >
                    Close
                  </Button>
                </div>
              </>
            )}
          </form>
        </div>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog fullWidth={true} maxWidth={"sm"} open={openDeleteModal}>
        <div className="bg-white w-full px-5">
          <div className="modal-head flex items-center justify-end pt-2">
            <div onClick={handleCloseDeleteModal}>
              <IconButton>
                <CloseIcon />
              </IconButton>
            </div>
          </div>
          <form className="space-y-4" onSubmit={handleDeleteBlog}>
            <div className="space-y-7">
              <div>
                <h1 className="text-2xl text-red-700 font-semibold text-center">
                  Are You Sure You Want To Delete?
                </h1>
              </div>
            </div>
            <div className="space-x-4 text-center pb-5">
              {setDeleteData.isLoading ? (
                <Button type="button">
                  <Spinner />
                </Button>
              ) : (
                <Button type="submit" variant="contained" color={`error`}>
                  Delete
                </Button>
              )}

              <Button
                variant="outlined"
                color="info"
                onClick={handleCloseDeleteModal}
              >
                Close
              </Button>
            </div>
          </form>
        </div>
      </Dialog>
    </>
  );
}

export default UpdateBlogs;
